import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Company Profile DTO (Tab 1 - Company Details)
 */
export interface UpdateCompanyProfileDto {
  name?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  country_code?: string;
  vat_number?: string;
  description?: string;
  registrationCode?: string;
}

/**
 * Company Settings DTO (Tab 2 - Settings)
 */
export interface UpdateCompanySettingsDto {
  autoAcceptOrders?: boolean;
  orderTimeout?: number;
  maxDriverDistance?: number;
  commissionRate?: number;
  emailNotifications?: boolean;
  smsNotifications?: boolean;
  pushNotifications?: boolean;
}

/**
 * Company Profile Response
 */
export interface CompanyProfileResponse {
  id: number;
  name: string;
  email: string;
  phone: string;
  parent_id?: number | null;
  website?: string;
  address?: string;
  city?: string;
  country_code?: string;
  vat_number?: string;
  description?: string;
  registrationCode?: string;
  settings?: Record<string, string>;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyProfileService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/company/profile`;

  /**
   * Get company profile (own company)
   * @returns Observable with company profile data
   */
  getProfile(): Observable<ApiResponse<CompanyProfileResponse>> {
    return this.http.get<ApiResponse<CompanyProfileResponse>>(this.apiUrl);
  }

  /**
   * Update company profile (Tab 1 - Company Details)
   * @param data Company profile data to update
   * @returns Observable with updated company
   */
  updateProfile(data: UpdateCompanyProfileDto): Observable<ApiResponse<CompanyProfileResponse>> {
    return this.http.put<ApiResponse<CompanyProfileResponse>>(this.apiUrl, data);
  }

  /**
   * Get company settings (from company_settings table)
   * @returns Observable with settings as Record<string, string>
   */
  getSettings(): Observable<ApiResponse<Record<string, string>>> {
    return this.http.get<ApiResponse<Record<string, string>>>(`${this.apiUrl}/settings`);
  }

  /**
   * Update company settings (new format with array of {key, value})
   * @param settings Array of settings to update
   * @returns Observable with updated settings
   */
  updateSettings(settings: Array<{key: string, value: string}>): Observable<ApiResponse<Record<string, string>>> {
    return this.http.put<ApiResponse<Record<string, string>>>(`${this.apiUrl}/settings`, { settings });
  }
}
